import { queryDaBusca } from '../utils/minhasBuscas';
import HistoricoBuscas from './HistoricoBuscas';
import LeilaGuiada from './LeilaGuiada';
import { valorMonetario } from '../utils/valorMonetario';
// src/components/Leila.js
//
// A Leila — assistente de busca do Leilancer. O nome vem de Leilancer.
//
// Por dentro é a busca de sempre: a pessoa escreve "apartamento em Curitiba até
// 300 mil" e recebe os imóveis. A conversa substitui o formulário de filtros,
// não a busca.
//
// Ela sempre diz em palavras o que entendeu ("apartamento, em Curitiba/PR, até
// R$ 300.000") antes de mostrar o resultado. Sem isso, uma interpretação errada
// vira "a busca não presta" em vez de "ela me entendeu errado, vou corrigir".
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Minus, Send, Search, Bell, Check, ListFilter } from 'lucide-react';
import { rotaAnalise, rotaBusca } from '../utils/rotas';

// Monograma "LL" da marca, o mesmo do favicon. Serve no circulo pequeno onde a
// marca horizontal completa nao caberia.
const marcaLL = '/favicon-192.png';

// O retrato da Leila. Se o arquivo ainda não estiver publicado, o componente
// volta sozinho para o monograma da marca — melhor a logo do que um quadrado
// quebrado no canto da tela de quem está usando.
const retratoLeila = '/leila.jpg';

// Fora do componente de propósito: aqui dentro ele seria recriado a cada tecla
// digitada no campo, e a imagem remontaria piscando no meio da conversa.
//
// Um rosto e uma logo não se enquadram igual — o retrato preenche o círculo, o
// monograma precisa de folga para não encostar na borda.
function Rosto({ semRetrato, aoFalhar, className = '' }) {
  if (semRetrato) {
    return <img src={marcaLL} alt="" className={`object-contain p-[15%] ${className}`} />;
  }
  return (
    <img src={retratoLeila} alt="" onError={aoFalhar} className={`object-cover ${className}`} />
  );
}

const SUGESTOES = [
  'apartamento em Curitiba até 300 mil',
  'imóvel da Caixa até 300 mil',
  'casa no PR com 50% de desconto',
  'apartamento em Moema',
  'terreno até 100 mil',
];

// Filtros que a busca entende e o alerta não acompanha. Aparecem por extenso na
// confirmação, para a pessoa saber que o aviso vai ser mais amplo que a busca.
const FORA_DO_ALERTA = {
  descontoMin: 'desconto mínimo',
  aceitaFinanciamento: 'financiamento',
  aceitaFgts: 'FGTS',
};

// A tela de busca lê os filtros da URL, mas fala a própria língua: os tipos
// são os grupos dela ("Terreno / Lote", não "terreno") e financiamento/FGTS
// valem "1", não "true". Sem traduzir, o filtro chegava e era descartado em
// silêncio — a busca abria mais ampla do que a conversa.
//
// "Área industrial" não tem grupo próprio na tela; Galpão é o vizinho mais
// próximo e é o que a tela oferece para esse tipo de imóvel.
const brl = (v) => {
  const n = valorMonetario(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
};

export default function Leila() {
  const API_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const location = useLocation();
  const [aberta, setAberta] = useState(false);
  const [recolhida, setRecolhida] = useState(false);
  const [historicoAberto, setHistoricoAberto] = useState(false);
  const [historicoVersao, setHistoricoVersao] = useState(0);
  const [texto, setTexto] = useState('');
  const [pensando, setPensando] = useState(false);
  const SAUDACAO = {
    de: 'leila',
    texto: 'Oi! Sou a Leila. Me diga o que você procura — o tipo de imóvel, a cidade e '
      + 'quanto pretende investir — que eu busco pra você.',
  };
  const [mensagens, setMensagens] = useState([SAUDACAO]);
  // O que já foi conversado. Vai e volta a cada mensagem para a Leila lembrar
  // do que a pessoa disse antes — é o que faz "e até 400?" e "agora São Paulo"
  // funcionarem sem a pessoa ter de repetir o resto.
  const [contexto, setContexto] = useState(null);
  const buscaAtual = useRef(null);
  const [sugestoesPessoais,setSugestoesPessoais] = useState([]);
  useEffect(()=>{
    if(!aberta || typeof axios.get !== 'function')return;
    let ativo=true;
    axios.get(`${API_URL}/api/leila/sugestoes`,{headers:{Authorization:`Bearer ${sessionStorage.getItem('token')}`}})
      .then(r=>{if(ativo)setSugestoesPessoais(r.data.sugestoes||[]);}).catch(()=>{});
    return()=>{ativo=false;};
  },[aberta,API_URL]);
  useEffect(() => {
    const atualizar = e => { buscaAtual.current = e.detail; };
    window.addEventListener('leila:busca-atual', atualizar);
    return () => window.removeEventListener('leila:busca-atual', atualizar);
  }, []);
  const [semRetrato, setSemRetrato] = useState(false);

  const fimRef = useRef(null);
  const conversaRef = useRef(null);
  const rolagemConversa = useRef(0);
  const campoRef = useRef(null);

  useEffect(() => {
    fimRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [mensagens, pensando]);

  useEffect(() => {
    if (aberta) {
      if (conversaRef.current) conversaRef.current.scrollTop = rolagemConversa.current;
      campoRef.current?.focus();
    }
  }, [aberta]);

  // O banner de consentimento fica fixo no rodapé e cobria o botão da Leila.
  // Quem vê esse banner é justamente o visitante de primeira sessão — o pior
  // momento possível para esconder a assistente. Aqui medimos a altura real
  // dele e subimos o botão; quando o banner some, o botão volta ao lugar.
  const [alturaBanner, setAlturaBanner] = useState(0);
  useEffect(() => {
    const medir = () => {
      const b = document.getElementById('leilancer-cookie-consent');
      if (!b) { setAlturaBanner(0); return; }
      // Não dá para usar offsetParent aqui: ele é sempre null em elementos
      // position:fixed, e o banner é fixo — o teste dava "escondido" sempre.
      const r = b.getBoundingClientRect();
      const escondido = getComputedStyle(b).display === 'none' || r.height === 0;
      setAlturaBanner(escondido ? 0 : Math.round(r.height));
    };
    medir();
    // O banner nasce depois do React e some ao ser respondido, então observamos
    // o body em vez de medir uma vez só.
    const obs = new MutationObserver(medir);
    obs.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', medir);
    return () => { obs.disconnect(); window.removeEventListener('resize', medir); };
  }, []);

  const afastamento = alturaBanner ? alturaBanner + 16 : 20;

  // Recolher libera a tela sem apagar a conversa, o contexto ou o rascunho.
  const recolher = () => {
    rolagemConversa.current = conversaRef.current?.scrollTop || 0;
    setRecolhida(true);
    setAberta(false);
  };

  // Esc recolhe: o painel cobre parte da tela no celular.
  useEffect(() => {
    const aoTeclar = (e) => { if (e.key === 'Escape' && conversaRef.current) { rolagemConversa.current = conversaRef.current.scrollTop; setRecolhida(true); setAberta(false); } };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, []);

  const recomecar = () => {
    setContexto(null);
    setMensagens([SAUDACAO]);
    setTexto('');
    campoRef.current?.focus();
  };

  const perguntar = async (frase) => {
    const pergunta = String(frase ?? texto).trim();
    if (!pergunta || pensando) return;

    setMensagens((m) => [...m, { de: 'eu', texto: pergunta }]);
    setTexto('');
    setPensando(true);

    try {
      window.dispatchEvent(new Event('leila:pedir-busca-atual'));
      const { data } = await axios.post(
        `${API_URL}/api/leila/buscar`,
        { mensagem: pergunta, contexto, buscaAtual: buscaAtual.current },
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
      );
      setContexto(data.contexto || null);
      setHistoricoVersao(v => v + 1);
      setMensagens((m) => [...m, {
        de: 'leila',
        texto: data.resposta,
        leiloes: data.leiloes || [],
        filtros: data.filtros || {},
        modo: data.modo,
        calculoId: data.calculoId,
        sugestoes: data.sugestoes || [],
        buscaId: data.buscaId,
        total: data.total || 0,
      }]);
      // Pediu a lista completa: abre a tela de busca com os filtros e a ordem.
      // Um instante de espera para a confirmação aparecer antes de a tela mudar.
      if (data.modo === 'abrir_busca') setTimeout(() => abrirNaBusca(data.filtros), 700);
    } catch (e) {
      const bloqueada = e?.response?.status === 403 && e?.response?.data?.code === 'PLANO_NECESSARIO';
      setMensagens((m) => [...m, {
        de: 'leila',
        texto: bloqueada
          ? 'A Leila faz parte do teste gratuito e dos planos ativos. Reative para voltar a usar.'
          : e?.response?.data?.message || 'Tive um problema para buscar agora. Pode tentar de novo?',
        bloqueada,
      }]);
    } finally {
      setPensando(false);
    }
  };

  // Transforma a busca que acabou de rodar num alerta, sem formulário. É a
  // hipótese de recorrência em um clique: quem pede para ser avisado tem motivo
  // para voltar no dia seguinte. Até aqui salvar exigia ir a outra página e
  // preencher tudo de novo — só 3 pessoas fizeram.
  const marcar = (i, extra) =>
    setMensagens((ms) => ms.map((m, j) => (j === i ? { ...m, ...extra } : m)));

  const avisar = async (i) => {
    const m = mensagens[i];
    if (!m?.filtros || m.alerta === 'salvando' || m.alerta === 'ok') return;
    // Ordenação não é filtro: não faz sentido num alerta nem em "ficou de fora".
    const query = { ...m.filtros };
    delete query.ordenarPor;
    marcar(i, { alerta: 'salvando' });
    try {
      const { data } = await axios.post(
        `${API_URL}/api/filtros/alertas/criar`,
        { query, notify: true },
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
      );
      const fora = (data.ignorados || []).map((k) => FORA_DO_ALERTA[k]).filter(Boolean);
      marcar(i, { alerta: 'ok', fora });
    } catch (e) {
      marcar(i, {
        alerta: 'erro',
        alertaErro: e?.response?.status === 403
          ? 'Você já usou todos os alertas do seu plano.'
          : 'Não consegui criar o aviso agora. Tente de novo em instantes.',
      });
    }
  };

  // Leva a conversa para a tela de busca completa, onde dá para ordenar por
  // preço, desconto e data da praça e refinar os filtros.
  //
  // A tela lê os filtros da URL só quando abre. Se a pessoa já está nela, trocar
  // a URL não muda nada na tela — então recarregamos. Vindo de outra página,
  // a navegação normal basta.
  const abrirNaBusca = (filtros, buscaSalvaId = null) => {
    const destino = rotaBusca(location.pathname);
    const qs = queryDaBusca(filtros) + (buscaSalvaId ? `&buscaSalva=${buscaSalvaId}` : '');
    const url = qs ? `${destino}?${qs}` : destino;
    setAberta(false);
    if (location.pathname === destino) window.location.assign(url);
    else navigate(url);
  };

  // Abre a ANALISE, nao a ficha: e nela que estao os numeros. E fecha a
  // conversa, que no celular cobriria a pagina de destino.
  const abrirAnalyser = () => {
    window.dispatchEvent(new Event('leila:pedir-busca-atual'));
    setAberta(false);
    if(document.querySelector('.lr-ferramenta')) { window.dispatchEvent(new Event('leila:investigar-riscos')); return; }
    const filtros=buscaAtual.current?.filtros || contexto?.filtros || {};
    navigate(`${rotaBusca(location.pathname)}?${queryDaBusca(filtros)}`, {state:{investigarRiscos:true}});
  };
  const abrirImovel = (id) => { setAberta(false); navigate(rotaAnalise(location.pathname, id)); };

  return (
    <>
      {/* Botão flutuante */}
      {!aberta && (
        <button
          onClick={() => { setAberta(true); setRecolhida(false); }}
          aria-label={recolhida ? "Retomar conversa com a Leila" : "Abrir a Leila, assistente de busca"}
          style={{ bottom: afastamento }}
          className="fixed right-5 z-[900] group flex items-center gap-3 rounded-full
                     bg-[#141414] ring-1 ring-white/15 hover:ring-primary/70
                     pl-1.5 pr-2 py-1.5 shadow-2xl transition
                     max-w-[calc(100vw-2.5rem)]"
        >
          {/* O rosto é o atrativo: em disco grande, com aro dourado. O disco
              branco também salva o monograma da reserva, que é dourado e
              sumiria contra o aro. */}
          <span className="w-9 h-9 rounded-full bg-white overflow-hidden flex-shrink-0
                           ring-2 ring-primary flex items-center justify-center">
            <Rosto semRetrato={semRetrato} aoFalhar={() => setSemRetrato(true)} className="w-full h-full" />
          </span>

          <span className="text-[14px] font-semibold text-primary">{recolhida ? "Retomar Leila" : "Leila"}</span>

          <span className="w-9 h-9 rounded-full bg-primary text-white flex items-center
                           justify-center flex-shrink-0 group-hover:opacity-90">
            <Search className="w-4 h-4" />
          </span>
        </button>
      )}

      {aberta && (
        <div
          style={{ '--afastamento': `${afastamento}px` }}
          className="fixed z-[900] bg-white shadow-2xl flex flex-col
                     inset-x-0 bottom-0 h-[85vh] rounded-t-2xl
                     sm:inset-auto sm:bottom-[var(--afastamento)] sm:right-5 sm:w-[400px] sm:h-[600px] sm:rounded-2xl"
          role="dialog"
          aria-label="Leila, assistente de busca"
        >
          {/* Cabeçalho */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full border border-gray-200 bg-white
                              flex items-center justify-center overflow-hidden flex-shrink-0">
                <Rosto semRetrato={semRetrato} aoFalhar={() => setSemRetrato(true)} className="w-full h-full" />
              </div>
              <div>
                <p className="font-bold text-gray-900 leading-tight">Leila</p>
                <p className="text-[11px] text-gray-500 leading-tight">assistente da Leilancer</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {/* Agora que a Leila lembra do que foi dito, é preciso poder
                  esquecer: sem isso um critério antigo seguiria valendo numa
                  busca nova sem a pessoa entender por quê. */}
              {contexto && (
                <button
                  onClick={recomecar}
                  className="px-2 py-1.5 rounded-lg hover:bg-gray-100 text-gray-500 text-xs"
                  aria-label="Começar uma conversa nova"
                >
                  Recomeçar
                </button>
              )}
              <button
                type="button"
                onClick={recolher}
                className="inline-flex items-center gap-1 px-2 py-2 rounded-lg hover:bg-gray-100 text-gray-600 text-xs"
                aria-label="Recolher conversa da Leila"
                title="Recolher para ver os imóveis; sua conversa será mantida"
              >
                <Minus className="w-4 h-4" aria-hidden="true" />
                Recolher
              </button>
            </div>
          </div>

          <div className="px-4 py-2 border-b border-gray-200"><LeilaGuiada rotulo="Investigações, favoritos e comparação" /></div>
          <button type="button" onClick={abrirAnalyser} className="mx-4 my-2 px-3 py-2 text-left text-sm font-semibold rounded-lg border border-amber-500 text-gray-900 bg-amber-50">Leilancer Analyser - Investigação aprofundada</button>
          {/* Conversa */}
          <div ref={conversaRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-gray-50">
            <div className="mb-3">
              <button type="button" className="text-sm font-semibold text-gray-600 underline" aria-expanded={historicoAberto} onClick={() => setHistoricoAberto(v => !v)}>Minhas buscas</button>
              {historicoAberto && <HistoricoBuscas compacto atualizacao={historicoVersao} aoRepetir={b => abrirNaBusca(b.filtros)} aoDuplicar={b => { setAberta(false); navigate(`${location.pathname.startsWith('/admin') ? '/admin' : '/dashboard'}/minhas-buscas`, { state: { duplicar: b } }); }} />}
            </div>
            {mensagens.map((m, i) => (
              <div key={i}>
                <div className={m.de === 'eu' ? 'flex justify-end' : ''}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.de === 'eu'
                      ? 'bg-primary text-white rounded-br-sm'
                      : 'bg-white text-gray-800 border border-gray-200 rounded-bl-sm'
                  }`}>
                    {m.texto}
                  </div>
                </div>

                {m.sugestoes?.map((s,i)=><button key={i} title={s.motivo} onClick={()=>abrirNaBusca(s.filtros)} className="block mt-2 text-sm font-semibold text-primary">{s.titulo}</button>)}
                {m.calculoId && <button className="mt-2 text-sm font-semibold text-primary" onClick={()=>abrirImovel(m.calculoId)}>Calcular LC-{m.calculoId}</button>}
                {m.modo === 'orientacao'  && <div className="mt-2"><LeilaGuiada rotulo="Abrir investigação guiada" /></div>}
                {m.modo === 'riscos' && <button className="mt-2 px-3 py-2 rounded-lg border border-current" onClick={abrirAnalyser}>Leilancer Analyser - Investigação aprofundada</button>}
                {m.bloqueada && (
                  <button
                    onClick={() => { setAberta(false); navigate('/planos'); }}
                    className="mt-2 text-sm font-semibold text-primary hover:underline"
                  >
                    Ver planos
                  </button>
                )}

                {m.de === 'leila' && Object.keys(m.filtros || {}).length > 0 && <div aria-label="Filtros interpretados pela Leila" className="flex flex-wrap gap-1 mt-2">{Object.entries(m.filtros).filter(([key]) => ['estado', 'cidade', 'bairro', 'tipoImovel', 'valorMin', 'valorMax', 'descontoMin'].includes(key)).map(([key, value]) => <span key={key} className="rounded-full border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700">{({ estado: 'Estado', cidade: 'Cidade', bairro: 'Bairro', tipoImovel: 'Tipo', valorMin: 'Valor mínimo', valorMax: 'Valor máximo', descontoMin: 'Desconto mínimo (%)' })[key]}: {['valorMin', 'valorMax'].includes(key) ? Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : Array.isArray(value) ? value.join(', ') : String(value)}</span>)}</div>}
                {!!m.leiloes?.length && (
                  <div className="mt-3 space-y-2">
                    {m.leiloes.map((l) => {
                      const lance = brl(l.valor_lance);
                      const ref = brl(l.valor_mercado || l.valor_avaliacao);
                      return (
                        <div key={l.id} className="space-y-2">
                        <button
                          onClick={() => abrirImovel(l.id)}
                          className="w-full text-left bg-white border border-gray-200 rounded-xl p-3
                                     hover:border-primary hover:shadow-sm transition flex gap-3"
                        >
                          {l.imagem && (
                            <img
                              src={l.imagem}
                              alt=""
                              loading="lazy"
                              className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-[15px] font-semibold text-gray-900 line-clamp-2 leading-snug">
                              {l.titulo}
                            </p>
                            <p className="text-[14px] text-gray-500 truncate mt-0.5">
                              {[l.cidade, l.estado].filter(Boolean).join('/')}
                            </p>
                            {!l.data_leilao && !l.primeira_praca_data && !l.segunda_praca_data && <p className="text-xs text-amber-900 mt-1">Data não informada · confira a situação no leiloeiro.</p>}
                            {lance && (
                              <p className="text-sm font-bold text-primary mt-1">
                                Lance: {lance}
                                {ref && <span className="block text-[14px] font-normal text-gray-600 mt-1">Referência: {ref}</span>}
                              </p>
                            )}
                          </div>
                        </button>
                        <LeilaGuiada id={l.id} titulo={l.titulo} />
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Doze imóveis num painel lateral não bastam para decidir. Quando
                    há mais, a pessoa pode levar a busca para a tela completa. */}
                {m.de === 'leila' && m.modo === 'busca' && m.total > (m.leiloes?.length || 0) && (
                  <button
                    onClick={() => abrirNaBusca(m.filtros)}
                    className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-[13px]
                               font-semibold text-white bg-primary rounded-xl px-3 py-2 hover:opacity-90 transition"
                  >
                    <ListFilter className="w-4 h-4" />
                    Ver todos os {m.total.toLocaleString('pt-BR')} com filtros e ordenação
                  </button>
                )}

                {/* Aparece também quando a busca não acha nada: "não tem hoje, te
                    aviso quando aparecer" é o melhor motivo para voltar. */}
                {m.de === 'leila' && m.modo === 'busca' && Object.keys(m.filtros || {}).length > 0 && (
                  <div className="mt-2">
                    {m.buscaId && <p className="text-xs text-gray-500">Registrada em Minhas buscas.</p>}
                    {m.alerta === 'ok' ? (
                      <p className="text-[12px] text-emerald-700 flex items-start gap-1.5">
                        <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span>
                          Pronto. Te aviso quando entrar imóvel novo assim.
                          {m.fora?.length > 0 && (
                            <span className="text-gray-500">
                              {' '}O aviso não filtra por {m.fora.join(' nem ')}.
                            </span>
                          )}
                        </span>
                      </p>
                    ) : (
                      <button
                        onClick={() => avisar(i)}
                        disabled={m.alerta === 'salvando'}
                        className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-primary
                                   border border-primary/40 rounded-full px-3 py-1.5 hover:bg-primary/5
                                   disabled:opacity-60 transition"
                      >
                        <Bell className="w-3.5 h-3.5" />
                        {m.alerta === 'salvando'
                          ? 'Criando aviso...'
                          : m.leiloes?.length
                            ? 'Me avise quando aparecer outro assim'
                            : 'Me avise quando aparecer'}
                      </button>
                    )}
                    {m.alerta === 'erro' && (
                      <p className="text-[12px] text-red-600 mt-1">{m.alertaErro}</p>
                    )}
                  </div>
                )}
              </div>
            ))}

            {pensando && (
              <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-3.5 py-2.5
                              text-sm text-gray-500 inline-block">
                Buscando...
              </div>
            )}

            {/* Sugestões só no começo, quando a pessoa ainda não sabe como falar com ela */}
            {mensagens.length === 1 && !pensando && (
              <div className="space-y-2 pt-1">
                {!!sugestoesPessoais.length && <div><p className="text-xs text-gray-500 mb-2">Com base nas suas buscas recentes</p>{sugestoesPessoais.map((s,i)=><button key={i} title={s.motivo} onClick={()=>abrirNaBusca(s.filtros)} className="block w-full text-left text-[13px] bg-white border border-gray-200 rounded-xl px-3 py-2 mb-2">{s.titulo}</button>)}</div>}
                {SUGESTOES.map((s) => (
                  <button
                    key={s}
                    onClick={() => perguntar(s)}
                    className="block w-full text-left text-[13px] bg-white border border-gray-200
                               rounded-xl px-3 py-2 text-gray-700 hover:border-primary transition"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <div ref={fimRef} />
          </div>

          {/* Campo */}
          <form
            onSubmit={(e) => { e.preventDefault(); perguntar(); }}
            className="flex items-center gap-2 p-3 border-t border-gray-200 flex-shrink-0 bg-white"
          >
            <input
              ref={campoRef}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Ex: casa em Londrina até 400 mil"
              className="flex-1 min-w-0 bg-gray-100 border border-gray-200 rounded-xl px-3 py-2.5
                         text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-primary"
            />
            <button
              type="submit"
              disabled={!texto.trim() || pensando}
              className="p-2.5 rounded-xl bg-primary text-white disabled:opacity-40 flex-shrink-0"
              aria-label="Enviar"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

